/**
 * AMD-13 — the status line. dwarf.land prints `(569, 271) ⛰ Hills 41.2N 77.6W` along
 * the bottom of its map: coordinates and terrain, dense, unglamorous, precise. This is
 * the same idea for a run.
 *
 * It is not decoration. Seed and content hash are exactly the two fields the §13.1.10
 * share string is built from, so a player reading a screenshot of a Pause panel has
 * everything needed to reproduce the run. Tick is the sim clock the seed replays
 * against; arena, phase and difficulty are the rest of the run's coordinates.
 *
 * Cells are separate nodes so the mono grid can hold each one to a fixed column and
 * so `setText` only touches the cell that actually changed.
 */

import { div, setText, span } from './dom';
import { formatSeed, formatTime, humanizeId } from './format';

interface Cell {
  root: HTMLElement;
  value: HTMLElement;
}

export interface StatusFields {
  seed: number;
  tick: number;
  arena_id: string;
  phase_id: string;
  difficulty_id: string;
  content_version: string;
}

export class StatusLine {
  readonly root: HTMLElement;
  private seed: Cell;
  private tick: Cell;
  private arena: Cell;
  private phase: Cell;
  private diff: Cell;
  private hash: Cell;

  constructor() {
    this.root = div('status-line');
    this.root.setAttribute('role', 'status');
    this.seed = this.cell('seed', 'key');
    this.tick = this.cell('t', 'num');
    this.arena = this.cell('arena', '');
    this.phase = this.cell('phase', '');
    this.diff = this.cell('diff', '');
    this.hash = this.cell('build', 'num');
  }

  private cell(label: string, mod: string): Cell {
    const root = div('status-cell' + (mod ? ' is-' + mod : ''));
    root.appendChild(span('status-key', label));
    const value = span('status-value', '—');
    root.appendChild(value);
    this.root.appendChild(root);
    return { root, value };
  }

  /** Panel-open only. Nothing here runs while the sim is stepping. */
  set(f: StatusFields): void {
    setText(this.seed.value, formatSeed(f.seed));
    // Both forms: the tick is what the sim counts, the clock is what a player reads.
    setText(this.tick.value, String(f.tick >>> 0) + ' (' + formatTime(f.tick / 60) + ')');
    setText(this.arena.value, f.arena_id ? humanizeId(f.arena_id) : 'Trench Floor');
    setText(this.phase.value, f.phase_id ? humanizeId(f.phase_id) : 'Descent');
    setText(this.diff.value, f.difficulty_id ? humanizeId(f.difficulty_id) : 'Standard');
    setText(this.hash.value, '#' + f.content_version);
  }
}
